import { Box, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SpaceStateFilterValues } from 'src/features/spaces/constants/space-state/space-state-filter-values';
import { spaceStateTranslations } from 'src/features/spaces/constants/space-state/space-state-translations';
import { spaceTypesTranslations } from 'src/features/spaces/constants/space-types-translations';
import { SpaceState } from 'src/features/spaces/types/space-state';
import { SpaceType } from 'src/features/spaces/types/space-type';
import { HandleChangeFilterValue } from 'src/types/handle-change-filter-value';
import { Filters } from 'src/types/interfaces/filters';
import { toggleFilterItemSelection } from 'src/utils/filters/toggle-filter-item-selection';

interface Props {
  filters: Filters;
  handleChangeFilterValue: HandleChangeFilterValue;
}

export const SpacesFilterChips = ({
  filters,
  handleChangeFilterValue,
}: Props) => {
  const { t } = useTranslation(['spaces']);

  const renderFilterChips = () =>
    Object.entries(filters)?.map(
      ([filterType, { value: filterValues, operator }]) => {
        if (!filterValues) {
          return null;
        }

        if (Array.isArray(filterValues)) {
          return filterValues?.map(value => {
            const spaceState = Object.entries(SpaceStateFilterValues).find(
              ([, filterValue]) => filterValue === value,
            )?.[0];

            switch (filterType) {
              case 'state':
                return (
                  <Chip
                    key={value.toString()}
                    sx={{ mr: 1, mb: 1 }}
                    color="primary"
                    label={
                      <Typography
                        variant="caption"
                        sx={{ textTransform: 'capitalize' }}>
                        {`${t('spaces:common.state.title')}: ${t(spaceStateTranslations[spaceState as SpaceState])}`}
                      </Typography>
                    }
                    size="small"
                    onDelete={() => {
                      toggleFilterItemSelection(
                        filterType,
                        value.toString(),
                        false,
                        filters,
                        handleChangeFilterValue,
                        operator,
                      );
                    }}
                  />
                );

              case 'type':
                return (
                  <Chip
                    key={value.toString()}
                    sx={{ mr: 1, mb: 1 }}
                    color="primary"
                    label={
                      <Typography
                        variant="caption"
                        sx={{ textTransform: 'capitalize' }}>
                        {`${t('spaces:common.type')}: ${t(spaceTypesTranslations[value as SpaceType])}`}
                      </Typography>
                    }
                    size="small"
                    onDelete={() => {
                      toggleFilterItemSelection(
                        filterType,
                        value.toString(),
                        false,
                        filters,
                        handleChangeFilterValue,
                        operator,
                      );
                    }}
                  />
                );

              case 'spaceGroupToken':
                return null;

              default:
                return (
                  <Chip
                    key={value.toString()}
                    sx={{ mr: 1, mb: 1 }}
                    color="primary"
                    label={
                      <Typography
                        variant="caption"
                        sx={{ textTransform: 'capitalize' }}>
                        {value}
                      </Typography>
                    }
                    size="small"
                    onDelete={() => {
                      toggleFilterItemSelection(
                        filterType,
                        value.toString(),
                        false,
                        filters,
                        handleChangeFilterValue,
                        operator,
                      );
                    }}
                  />
                );
            }
          });
        }
      },
    );

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyItems: 'flex-start',
        flexWrap: 'wrap',
        px: 3,
        pb: 1.5,
      }}>
      {renderFilterChips()}
    </Box>
  );
};
